import { useToastCountManager } from '@design-system/index'
import { css, keyframes } from '@emotion/react'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { observer } from 'mobx-react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { matchPath, Redirect, Route, useHistory, useLocation } from 'react-router'
import { Switch } from 'react-router-dom'
import { GlobalStyle } from 'src/styles'
import { envUtils } from 'src/utils/envUtils'

import { useAppLastActiveTimeoutEffect } from '@/utils'

import Button from './component/atoms/Button'
import DefaultPageTemplate from './component/templates/PageTemplate/DefaultPageTemplate'
import PageContentTemplate from './component/templates/PageTemplate/PageContentTemplate'
import type { LocalStorageDataT } from './constant'
import { ErrorBoundary, ErrorBoundaryFallback } from './ErrorBoundary'
import { useAppUpdateDetection } from './hooks/useAppUpdateDetection'
import { useBanner } from './hooks/useBanner'
import { useInspectWorkbooks } from './hooks/useInspectWorkbooks'
import { useLifeCycle } from './hooks/useLifeCycle'
import { useDataTracking } from './hooks/useMixpanel'
import { usePopup } from './hooks/usePopup'
import { useSavePath } from './hooks/useSavePath'
import { useSetStudentGroups } from './hooks/useSetStudentGroups'

const GlobalFadeToast = lazy(() => import('./component/atoms/GlobalFadeToast/GlobalFadeToast'))
const GlobalLoader = lazy(() => import('./component/atoms/GlobalLoader/GlobalLoader'))
const FloatingActionButtonWrap = lazy(
  () => import('./contentComponents/FloatingActionButton/FloatingActionButtonWrap'),
)
const ScreenSizeGuide = lazy(() => import('./contentComponents/global/ScreenSizeGuide'))

import ServerMaintenanceService from './page/etc/ServerMaintenance/_service/ServerMaintenance.service'
import commonRepo from './repository/common.repo'
import { RouteName, SCREEN_SIZE_GUIDE_EXCLUDE_ROUTES } from './router'
import { localStorageService } from './service/storage.service'
import { initAxiosDefaultBaseURL } from './utils/axios/util'

const Login = lazy(() => import('./page/etc/Login/Login'))
const Lesson = lazy(() => import('./page/Lesson'))
const FindPassword = lazy(() => import('./page/etc/Login/FindPassword/FindPassword'))
const Academy = lazy(() => import('./page/Academy'))
const LessonPreparation = lazy(() => import('./page/Lesson-prepare/LessonPreparePage'))
const MyPage = lazy(() => import('./page/etc/MyPage'))
const MakeWorksheet = lazy(() => import('./page/MakeWorksheet'))
const CreateUpdateClassManage = lazy(
  () => import('./page/Academy/AcademyManage/ClassManage/CreateUpdateClass'),
)
const WorksheetGroupScoring = lazy(() => import('./page/Lesson/Worksheet/WorksheetGroupScoring'))
const WorksheetDerivation = lazy(() => import('./page/Lesson/Worksheet/WorksheetDerivation'))
const GuestLoginPage = lazy(() => import('./page/etc/Login/GuestLogin/GuestLogin.page'))
const MathflatCallbackPage = lazy(() => import('./page/etc/MathflatCallback/MathflatCallback.page'))

const OnlyViewClassDetail = lazy(
  () => import('src/page/Academy/AcademyManage/ClassManage/CreateUpdateClass/OnlyViewClassDetail'),
)
const ServerMaintenance = lazy(() => import('./page/etc/ServerMaintenance/ServerMaintenance'))
const TallyEmbedPage = lazy(() => import('./page/etc/TallyEmbed/TallyEmbedPage'))
function App() {
  const history = useHistory()

  useEffect(() => {
    ServerMaintenanceService.checkServerMaintenance()
  }, []) // 서버 점검중인지 확인
  useSetStudentGroups() // 학생목록 최초 로드
  // ScienceFlat에서는 비활성화
  // useVirtualStudent() // (무료체험 + 학생 0명) 가상 학생 로드
  // useInspectWorkbooks() // 검정교과서 최초 로드
  // useBanner() // Banner 로드

  useSavePath() // 경로 저장
  useDataTracking() // mixpanel 트래킹 시작
  const popupProps = usePopup(
    envUtils.isLive ||
      !!localStorageService.getItem('POPUP_VISIBILITY_DEV_ONLY') ||
      commonRepo.isSeoltab,
  ) // 팝업 로직
  useToastCountManager()
  useAppUpdateDetection() // 앱 업데이트 감지

  useAppLastActiveTimeoutEffect({
    onTimeoutReturn: () => {
      if (commonRepo.isSeoltab) {
        return
      }
      history.push(RouteName.myWorksheet)
    },
  })

  const appState = useLifeCycle()

  const isStagingOrLive = envUtils.isLive || envUtils.isStaging

  const location = useLocation()

  // 간단한 fallback 결정 함수
  const getFallback = () => {
    return <PageContentTemplate.Fallback />
  }

  if (appState !== 'INITIALIZED') {
    return <></>
  }

  return (
    <>
      <GlobalStyle />
      <Suspense fallback={null}>
        <FloatingActionButtonWrap />
        <GlobalLoader />
        <GlobalFadeToast />
      </Suspense>
      {isStagingOrLive &&
        !SCREEN_SIZE_GUIDE_EXCLUDE_ROUTES.some((path) => {
          return matchPath(window.location.pathname, { path })
        }) && (
          <Suspense fallback={null}>
            <ScreenSizeGuide />
          </Suspense>
        )}
      {!isStagingOrLive && <DevelopToolbar popupProps={popupProps} />}

      <Suspense fallback={null}>
        <ErrorBoundary fallback={(reset) => <ErrorBoundaryFallback reset={reset} />}>
          <Switch>
            {/* public routes */}
            <Route path={RouteName.passwordReset} component={FindPassword} />
            <Route path={RouteName.login} component={Login} />
            <Route path={RouteName.serverMaintenance} component={ServerMaintenance} />
            <Route path={RouteName.guestLogin} component={GuestLoginPage} />
            <Route path={RouteName.seoltabLogin} component={GuestLoginPage} />
            <Route path={RouteName.mathflatCallback} component={MathflatCallbackPage} />

            {/* authenticated routes */}
            {/* full modal routes */}
            <Route path={RouteName.tallyEmbed} component={TallyEmbedPage} />
            <Route path={RouteName.makeWorksheet} component={MakeWorksheet} />
            <Route path={RouteName.lessonWorksheetGroupScoring} component={WorksheetGroupScoring} />
            <Route path={RouteName.lessonWorksheetDerivation} component={WorksheetDerivation} />

            <Route
              path={[RouteName.createClass, RouteName.modifyClass]}
              component={
                commonRepo.permission.get.manage.class.등록
                  ? CreateUpdateClassManage
                  : OnlyViewClassDetail
              }
            />

            {/* default template routes */}
            <Route
              path={[
                RouteName.lesson,
                RouteName.lessonPreparation,
                RouteName.academy,
                RouteName.myInfo,
              ]}
            >
              <DefaultPageTemplate>
                <Suspense fallback={getFallback()}>
                  <Switch>
                    <Route path={RouteName.academy} component={Academy} />
                    <Route path={RouteName.lesson} component={Lesson} />
                    <Route path={RouteName.lessonPreparation} component={LessonPreparation} />
                    <Route path={RouteName.myInfo} component={MyPage} />
                  </Switch>
                </Suspense>
              </DefaultPageTemplate>
            </Route>

            <Redirect to={RouteName.login} />
          </Switch>
        </ErrorBoundary>
      </Suspense>
    </>
  )
}
// 모듈 스코프
const sirenAlarm = keyframes`
  0%, 100% { box-shadow: 0 0 8px 3px red; }
  50% { background: transparent; box-shadow: 0 0 8px 3px transparent; }
`
const ApiChangeButton = () => {
  const [api, setApi] = useState(localStorageService.getItem('DEV_API') ?? 'dev')
  const order = ['dev', 'stg', 'live', 'custom'] as const

  const getNextPhase = (phase: LocalStorageDataT['DEV_API']) => {
    let index = order.findIndex((item) => item === phase)

    if (index < 0) {
      index = -1
    }

    return order[(index + 1) % order.length]
  }

  const changeApi = () => {
    jwtTokenService.clearTokens()
    const nextPhase = getNextPhase(api)
    let apiTypeOrBaseURL: LocalStorageDataT['DEV_API'] = nextPhase

    if (nextPhase === 'custom') {
      const custom = prompt(
        'http로 시작하는 커스텀 API DOMAIN을 입력해주세요.\n(ex: https://api-dev.scienceflat.com)\n* CORS 대응은 되어있지 않습니다.',
      )
      if (!custom || !custom.startsWith('http')) {
        apiTypeOrBaseURL = 'dev'
      } else {
        apiTypeOrBaseURL = custom
      }
    }

    if (nextPhase === 'custom') {
      jwtTokenService.setEnvironment(nextPhase, apiTypeOrBaseURL)
    } else {
      jwtTokenService.setEnvironment(nextPhase)
    }

    setApi(apiTypeOrBaseURL)
    initAxiosDefaultBaseURL(apiTypeOrBaseURL)
    commonRepo.router.push(RouteName.login)
  }

  return (
    <div
      css={
        api === 'live'
          ? css`
              width: fit-content;
              border-radius: 4px;
              animation: ${sirenAlarm} 1s infinite;
            `
          : undefined
      }
    >
      <Button
        variant="outline"
        color={api === 'live' ? 'error' : api === 'stg' ? 'accent' : 'neutral'}
        size="xs"
        onClick={changeApi}
      >
        {api}
      </Button>
    </div>
  )
}

type PopupButtonProps = {
  off: boolean
  togglePopup: () => void
}

const PopupButton = ({ off, togglePopup }: PopupButtonProps) => {
  return (
    <Button
      aria-pressed={!off}
      variant="outline"
      color={off ? 'neutral' : 'error'}
      size="xs"
      onClick={togglePopup}
      width={60}
    >
      <span style={{ fontSize: 12 }}>{off ? '팝업 OFF' : '팝업 ON'}</span>
    </Button>
  )
}

export default observer(App)

const DevelopToolbar = ({ popupProps }: { popupProps: PopupButtonProps }) => {
  return (
    <div
      css={{
        position: 'fixed',
        top: 90,
        left: 0,
        padding: 4,
        zIndex: 999999999,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <ApiChangeButton />
      <PopupButton {...popupProps} />
    </div>
  )
}
